import React, { useState } from 'react';
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';
import { IconTrash } from '@tabler/icons-react';
import type { TemplateFollowUpTodo } from '../../../types/ticketTypeTemplate';
import { EditorCard } from '../EditorCard';
import { Chip } from '../../chip';
import { FIELD_SX } from '../../../styles/designSystem';

/**
 * One follow-up to-do on a ticket-type template: created alongside every
 * ticket of this type, it raises a ticket from `ticketTypeId` on `dateField` +
 * `offsetDays` (Sydney). sm4rt-api validates the same rules on save — the
 * pickers here just keep a typo from reaching it.
 */

interface FollowUpTodoEditorProps {
  followUp: TemplateFollowUpTodo;
  /** Templates the follow-up ticket can be raised from. */
  targets: { ticketTypeId: string; displayName: string }[];
  /** This template's `date` fields — the only valid `dateField`s. */
  dateFields: { fieldKey: string; label: string }[];
  onChange: (updated: TemplateFollowUpTodo) => void;
  onDelete: () => void;
}

const PRIORITIES: NonNullable<TemplateFollowUpTodo['priority']>[] = ['low', 'medium', 'high'];

const offsetLabel = (days: number) =>
  days === 0 ? 'on' : `${Math.abs(days)} day${Math.abs(days) === 1 ? '' : 's'} ${days > 0 ? 'after' : 'before'}`;

export const FollowUpTodoEditor: React.FC<FollowUpTodoEditorProps> = ({
  followUp,
  targets,
  dateFields,
  onChange,
  onDelete,
}) => {
  const [expanded, setExpanded] = useState(false);
  const set = <K extends keyof TemplateFollowUpTodo>(key: K, value: TemplateFollowUpTodo[K]) =>
    onChange({ ...followUp, [key]: value });

  const targetName =
    targets.find((t) => t.ticketTypeId === followUp.ticketTypeId)?.displayName ?? followUp.ticketTypeId;
  const dateLabel =
    dateFields.find((f) => f.fieldKey === followUp.dateField)?.label ?? followUp.dateField;

  return (
    <EditorCard
      expanded={expanded}
      onToggle={() => setExpanded((v) => !v)}
      title={
        <>
          <span style={{ fontWeight: 600, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {targetName || <em style={{ color: '#aaa', fontWeight: 400 }}>(no template)</em>}
          </span>
          <Chip tone="info">
            {offsetLabel(followUp.offsetDays)} {dateLabel || '(no date)'}
          </Chip>
        </>
      }
      actions={
        <button
          type="button"
          className="editor-icon-btn editor-icon-btn--danger"
          title="Delete follow-up"
          onClick={onDelete}
        >
          <IconTrash size={15} stroke={1.5} />
        </button>
      }
    >
      <Stack spacing={2}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <FormControl size="small" sx={[{ flex: 1 }, FIELD_SX]}>
            <InputLabel>Raise ticket from</InputLabel>
            <Select
              label="Raise ticket from"
              value={followUp.ticketTypeId}
              onChange={(e) => set('ticketTypeId', e.target.value)}
            >
              {targets.map((t) => (
                <MenuItem key={t.ticketTypeId} value={t.ticketTypeId}>
                  {t.displayName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={[{ width: 200 }, FIELD_SX]}>
            <InputLabel>From date field</InputLabel>
            <Select
              label="From date field"
              value={dateFields.some((f) => f.fieldKey === followUp.dateField) ? followUp.dateField : ''}
              onChange={(e) => set('dateField', e.target.value)}
            >
              {dateFields.map((f) => (
                <MenuItem key={f.fieldKey} value={f.fieldKey}>
                  {f.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            size="small"
            label="Days after"
            type="number"
            value={followUp.offsetDays}
            onChange={(e) => {
              const n = Number(e.target.value);
              if (Number.isInteger(n)) set('offsetDays', n);
            }}
            helperText="Negative = before"
            sx={[{ width: 120 }, FIELD_SX]}
          />
          <FormControl size="small" sx={[{ width: 130 }, FIELD_SX]}>
            <InputLabel>Priority</InputLabel>
            <Select
              label="Priority"
              value={followUp.priority ?? 'medium'}
              onChange={(e) => set('priority', e.target.value as TemplateFollowUpTodo['priority'])}
            >
              {PRIORITIES.map((p) => (
                <MenuItem key={p} value={p}>
                  {p}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
        <TextField
          size="small"
          label="To-do title"
          value={followUp.titleTemplate}
          onChange={(e) => set('titleTemplate', e.target.value)}
          fullWidth
          helperText="Supports {fieldKey} placeholders from this template's fields. Max 200 characters."
          slotProps={{ htmlInput: { maxLength: 200 } }}
          sx={FIELD_SX}
        />
      </Stack>
    </EditorCard>
  );
};
